import { cn } from "@listto/shared";
import { type Component, Show, splitProps } from "solid-js";
import { Button } from "@/components/ui/button";

interface DetailActionButtonProps {
  label: string;
  icon: Component<{ class?: string }>;
  onClick?: (e: MouseEvent) => void;
  isActive?: boolean;
  isDestructive?: boolean;
  class?: string;
  iconClass?: string;
  shortcut?: string;
  shortcutTitle?: string;
}

export function DetailActionButton(props: DetailActionButtonProps) {
  const [local, others] = splitProps(props, [
    "label",
    "icon",
    "onClick",
    "isActive",
    "isDestructive",
    "class",
    "iconClass",
    "shortcut",
    "shortcutTitle",
  ]);

  const getButtonClass = () => {
    if (local.isDestructive) {
      return "text-destructive hover:bg-destructive/10 hover:text-destructive";
    }
    if (local.isActive) {
      return "text-primary bg-primary/10";
    }
    return "hover:bg-muted/10 text-foreground/80";
  };

  const getOverlayClass = () => {
    if (local.isDestructive) {
      return "bg-destructive/5 group-active/btn:bg-destructive/10";
    }
    if (local.isActive) {
      return "bg-primary/10 group-active/btn:bg-primary/20";
    }
    return "bg-foreground/5 group-active/btn:bg-foreground/10";
  };

  const getKbdClass = () => {
    if (local.isActive) {
      return "border-border/40 bg-background text-foreground/90 group-hover/btn:border-primary/40 group-hover/btn:text-primary";
    }
    return "border-primary/20 bg-primary/5 text-primary group-hover/btn:border-primary/40 group-hover/btn:bg-primary/10";
  };

  const handleClick = (e: MouseEvent) => {
    local.onClick?.(e);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      class={cn(
        "w-full justify-start gap-3 h-11 px-3 rounded-lg transition-all duration-200 group/btn relative overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        getButtonClass(),
        local.class,
      )}
      onClick={handleClick}
      {...others}
    >
      <div
        class={cn(
          "absolute inset-0 opacity-0 group-hover/btn:opacity-100 group-active/btn:opacity-100 transition-all duration-200 pointer-events-none",
          getOverlayClass(),
        )}
      />
      <local.icon
        class={cn(
          "h-4.5 w-4.5 shrink-0 transition-transform duration-200",
          local.isActive &&
            !local.isDestructive &&
            "fill-current scale-110 drop-shadow-[0_0_8px_rgba(var(--accent-rgb),0.3)]",
          local.iconClass,
        )}
      />
      <span class="truncate">{local.label}</span>
      <Show when={local.shortcut}>
        <kbd
          class={cn(
            "ml-auto h-4.5 min-w-[15px] inline-flex items-center justify-center rounded px-1.5 font-mono text-[9px] font-bold transition-all duration-200 opacity-0 group-hover/btn:opacity-100 group-focus-within/btn:opacity-100 group-focus/btn:opacity-100 select-none cursor-help border",
            getKbdClass(),
          )}
          title={local.shortcutTitle}
        >
          {local.shortcut}
        </kbd>
      </Show>
    </Button>
  );
}
